import React, { useCallback, useEffect, useState } from 'react';
import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import { OtherShippingOptionsService } from '../../../services/other-shipping-options/other-shipping-options-service';
import {
  dangerousGoods,
  dangerousGoodsDhlEcommerce,
  dangerousGoodsLegacy,
  dangerousGoodsPurolator,
  dangerousGoodsSimple,
} from '../../../services/other-shipping-options/constants';
import { OtherShippingOptionsFormContainer } from './other-shipping-options-form-container';
import { BULK_UPDATE_FULFILLMENT_PLAN_ID } from '../constants';
import { emptyObject } from 'ui-components/default-props/empty';

const getChangedOptionsFromUpdate = (newOptions) => {
  return Object.keys(newOptions).reduce((acc, cur) => {
    if (cur !== 'sellerLabelProviderId' && newOptions[cur] && newOptions[cur].changed) {
      return {
        ...acc,
        [cur]: newOptions[cur],
      };
    }
    return acc;
  }, {});
};

const getOptionCheckedById = (id, options) => {
  return get(options, `${id}.checked`, false);
};

const calculateOptionState = (options, optionsState) => {
  return Object.keys(options).reduce((acc, key) => {
    return {
      ...acc,
      [key]: optionsState[key] ?? options[key],
    };
  }, {});
};

export const EditOtherShippingOptions = ({
  options = emptyObject,
  saveOptions,
  fulfillmentPlanId = null,
  sectionsToShow,
  sellerLabelProviderId: sellerLabelProviderIdProp = null,
}) => {
  const [optionsState, setOptionsState] = useState({
    ...options,
    sellerLabelProviderId: undefined,
  });

  useEffect(() => {
    setOptionsState((currentOptions) => calculateOptionState(options, currentOptions));
  }, [options]);

  const resetChangedValuesInState = useCallback(() => {
    setOptionsState((currentOptions) => {
      const resetObj = {
        ...currentOptions,
        sellerLabelProviderId: undefined,
      };
      Object.keys(currentOptions).forEach((key) => {
        if (currentOptions.changed && key !== 'sellerLabelProviderId') {
          resetObj[key] = {
            ...currentOptions[key],
            changed: false,
          };
        }
      });
      return resetObj;
    });
  }, []);

  const submit = useCallback(
    (newOptions) => {
      const selectedOptions = getChangedOptionsFromUpdate(newOptions);
      const { sellerLabelProviderId } = newOptions;
      resetChangedValuesInState();

      const translatedOptions = OtherShippingOptionsService.translateDataForSaving(selectedOptions);

      if (sellerLabelProviderId) {
        saveOptions(fulfillmentPlanId, {
          options: translatedOptions,
          sellerLabelProviderId,
        });
        return;
      }
      const hasUpdate = Object.keys(translatedOptions).length;
      if (!hasUpdate) {
        return;
      }
      saveOptions(fulfillmentPlanId, { options: translatedOptions });
    },
    [fulfillmentPlanId, resetChangedValuesInState, saveOptions],
  );

  const handleChange = useCallback(
    (id, value) => {
      const wasCheckboxToggled = !getOptionCheckedById(id, optionsState);

      const removeUnusedDangerousGoodsOptions = (optionId) => {
        const dangerousGoodsOptions = [
          dangerousGoods,
          dangerousGoodsPurolator,
          dangerousGoodsDhlEcommerce,
          dangerousGoodsLegacy,
          dangerousGoodsSimple,
        ];

        if (!dangerousGoodsOptions.includes(optionId)) {
          return {};
        }

        const invalidDangerousGoodsOptions = dangerousGoodsOptions.filter((dgOptionId) => dgOptionId !== optionId);

        return invalidDangerousGoodsOptions.reduce((acc, option) => {
          return {
            ...acc,
            [option]: {
              checked: false,
              value: null,
              changed: true,
            },
          };
        }, {});
      };

      const newOptions = {
        ...optionsState,
        [id]: {
          ...optionsState[id],
          checked: wasCheckboxToggled,
          value: value !== undefined ? value : wasCheckboxToggled,
          changed: true,
        },
        ...removeUnusedDangerousGoodsOptions(id),
      };
      submit(newOptions);
      setOptionsState(newOptions);
    },
    [submit, optionsState],
  );

  const handleInputUpdate = useCallback((data) => {
    setOptionsState((currentOptions) => ({
      ...currentOptions,
      [data.id]: {
        ...currentOptions[data.id],
        value: data.value,
      },
    }));
  }, []);

  const handleDataUpdate = useCallback(
    (data) => {
      const update = {
        [data.id]: {
          value: data.value,
          changed: true,
          checked: data.checked ?? getOptionCheckedById(data.id, optionsState),
        },
      };
      if (data.sellerLabelProviderId) {
        update.sellerLabelProviderId = data.sellerLabelProviderId;
      }

      const newOptions = {
        ...optionsState,
        ...update,
      };
      submit(newOptions);
      setOptionsState(newOptions);
    },
    [submit, optionsState],
  );

  if (!fulfillmentPlanId) {
    return null;
  }

  return (
    <OtherShippingOptionsFormContainer
      fulfillmentPlanId={fulfillmentPlanId}
      onInputUpdated={fulfillmentPlanId === BULK_UPDATE_FULFILLMENT_PLAN_ID ? handleDataUpdate : handleInputUpdate}
      onOptionChecked={handleChange}
      onOptionUpdated={handleDataUpdate}
      optionData={optionsState}
      sectionsToShow={sectionsToShow}
      sellerLabelProviderId={sellerLabelProviderIdProp}
    />
  );
};

EditOtherShippingOptions.propTypes = {
  options: PropTypes.shape({
    billToAccount: PropTypes.shape({
      checked: PropTypes.bool,
      value: PropTypes.shape({
        billToParty: PropTypes.string,
        accountNumber: PropTypes.string,
        billingCountry: PropTypes.string,
        billingPostalCode: PropTypes.string,
      }),
    }),
  }),
  saveOptions: PropTypes.func.isRequired,
  fulfillmentPlanId: PropTypes.string,
  sectionsToShow: PropTypes.arrayOf(PropTypes.string).isRequired,
  sellerLabelProviderId: PropTypes.string,
};
